import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { isAuthConfigured, loginRequest } from '@/auth/config';
import { takeReturnTo } from '@/auth/AuthProvider';

/**
 * The console's front door.
 *
 * Redirect, not popup: the workforce tenant enforces MFA tenant-wide (verified
 * on OD-18), and an MFA challenge in a popup is the flow most likely to be
 * eaten by a blocker on a machine nobody has configured for it.
 */
export function SignIn() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { t } = useTranslation();

  // ⚠️ Load-bearing, and its absence is not visible as an error.
  //
  // MSAL returns the browser to the URL the login request started from, which
  // is this screen. Without this the signed-in person lands back on the sign-in
  // button, having successfully authenticated — indistinguishable from a
  // rejected sign-in, except that nothing anywhere reports a failure.
  //
  // `takeReturnTo` is also the other half of `rememberReturnTo` in RequireAuth:
  // the guard records where someone was heading, and this is the only place
  // that spends it. Exported and never called, it silently means "always land
  // on the overview" — and here it meant "never leave sign-in".
  if (isAuthenticated) {
    return <Navigate to={takeReturnTo() ?? '/'} replace />;
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-4 px-6">
      <h1 className="text-2xl font-semibold">{t('signIn.title')}</h1>
      <p className="text-sm text-[var(--color-muted-foreground)]">{t('signIn.description')}</p>

      {isAuthConfigured ? (
        <Button onClick={() => void instance.loginRedirect(loginRequest)}>
          {t('signIn.action')}
        </Button>
      ) : (
        // Named variables rather than a generic "not configured": the person
        // hitting this is a developer with a fresh checkout, and the fix is
        // three environment variables they should not have to go source-diving
        // to discover.
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('signIn.unconfigured')}
        </p>
      )}
    </main>
  );
}
